import DeleteIcon from "@mui/icons-material/CloseRounded";
import { CircularProgress, IconButton, Paper, Stack } from "@mui/material";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import TypographyTooltipEllipsis from "@entrilia/shared/components/TypographyTooltipEllipsis";
import { formatFileSize } from "@entrilia/shared/utilities/fileHelper";
import type { FileContainerEditState } from "./fileContainerEditState";
import { removeFileAction } from "./fileContainerEditState";

interface RowModel {
  name: string;
  size: number;
}

const getColumns = ({ state, setState }: Props): GridColDef<RowModel>[] => [
  {
    field: "name",
    headerName: "Name",
    flex: 4,
    renderCell: ({ row }) => (
      <Stack direction="row" spacing={1} sx={{ width: "100%" }}>
        {state.uploadedFileName === row.name && <CircularProgress size={20} color="secondary" sx={{ flexShrink: 0 }} />}
        <TypographyTooltipEllipsis text={row.name} />
      </Stack>
    ),
  },
  {
    field: "size",
    headerName: "Size",
    flex: 1,
    valueFormatter: (value: number) => formatFileSize(value),
  },
  {
    field: "actions",
    headerName: "Actions",
    width: 72,
    align: "right",
    headerAlign: "right",
    cellClassName: "grid-row-actions",
    renderCell: ({ row }) => (
      <IconButton color="error" onClick={() => setState(removeFileAction(row.name))}>
        <DeleteIcon />
      </IconButton>
    ),
  },
];

interface Props {
  state: FileContainerEditState;
  setState: React.Dispatch<React.SetStateAction<FileContainerEditState>>;
}

const getRowId = (row: RowModel) => row.name;

const FileContainerFilesGrid = ({ state, setState }: Props) => {
  const columns = useMemo(() => getColumns({ state, setState }), [state, setState]);

  const rows = state.files.map(({ name, size }) => ({ name, size }));

  return (
    <Paper variant="outlined" sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <DataGrid<RowModel>
        rows={rows}
        columns={columns}
        getRowId={getRowId}
        disableColumnReorder
        disableColumnSorting
        disableColumnResize
      />
    </Paper>
  );
};

export default FileContainerFilesGrid;
